
import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { supabase } from "@/integrations/supabase/client";
import { Layout } from "@/components/layout/Layout";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { ArrowLeft, Calendar, User } from "lucide-react";
import { Separator } from "@/components/ui/separator";
import { toast } from "sonner";

interface BlogPost {
  id: string;
  title: string;
  description: string;
  content: string;
  category: string;
  featured: boolean;
  image_url: string | null;
  video_url: string | null;
  published_at: string;
  created_at: string;
}

export default function BlogPostPage() {
  const { t, i18n } = useTranslation();
  const { postId } = useParams<{ postId: string }>();
  const navigate = useNavigate();
  const [post, setPost] = useState<BlogPost | null>(null);
  const [loading, setLoading] = useState(true);

  // Fetch the single blog post
  useEffect(() => {
    const fetchPost = async () => {
      if (!postId) return;
      
      setLoading(true);
      try {
        const { data, error } = await supabase
          .from('blog_posts')
          .select('*')
          .eq('id', postId)
          .single();
        
        if (error) throw error;
        
        if (data) {
          setPost(data);
        } else {
          navigate('/blog'); // Redirect to blog listing if post not found
          toast.error(t('blog.postNotFound'));
        }
      } catch (error) {
        console.error('Error fetching post:', error);
        toast.error(t('blog.loadFailed'));
      } finally {
        setLoading(false);
      }
    };
    
    fetchPost();
  }, [postId, navigate, t]);

  // Format date
  const formattedDate = post?.published_at ? new Date(post.published_at).toLocaleDateString(
    i18n.language === 'ja' ? 'ja-JP' : 'en-US', 
    {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    }
  ) : '';

  return (
    <Layout>
      <div className="container max-w-4xl py-12">
        {loading ? (
          <div className="text-center py-12">
            <p>{t('common.loading')}</p>
          </div>
        ) : post ? (
          <div className="space-y-8">
            <Button 
              variant="ghost" 
              onClick={() => navigate('/blog')}
              className="mb-4"
            >
              <ArrowLeft className="mr-2 h-4 w-4" /> {t('common.back')}
            </Button>
            
            <div className="space-y-4">
              <div className="flex flex-wrap gap-2 items-center">
                <Badge variant="outline" className="bg-primary/10 text-primary hover:bg-primary/20">
                  {post.category}
                </Badge>
                <div className="flex items-center text-sm text-muted-foreground">
                  <Calendar className="h-3 w-3 mr-1" />
                  {formattedDate}
                </div>
              </div>
              
              <h1 className="text-3xl md:text-4xl font-bold">{post.title}</h1>
              
              <p className="text-muted-foreground text-lg">{post.description}</p>
              
              <div className="flex items-center text-sm">
                <User className="h-4 w-4 mr-1" />
                <span>{t('blog.admin.authorDefault')}</span>
              </div>
            </div>
            
            {post.image_url && (
              <div className="rounded-lg overflow-hidden aspect-video bg-muted">
                <img 
                  src={post.image_url} 
                  alt={post.title}
                  className="w-full h-full object-cover"
                />
              </div>
            )}
            
            {post.video_url && (
              <div className="rounded-lg overflow-hidden aspect-video bg-muted">
                <video 
                  src={post.video_url} 
                  controls
                  className="w-full h-full object-cover"
                >
                  {t('common.videoNotSupported')}
                </video>
              </div>
            )}
            
            <Separator />
            
            <Card className="p-6 md:p-8 prose dark:prose-invert max-w-none">
              {/* Render the content - in a real app, you would use a rich text editor or markdown renderer */}
              <div className="whitespace-pre-wrap">
                {post.content}
              </div>
            </Card>
          </div>
        ) : (
          <div className="text-center py-12">
            <p>{t('blog.postNotFound')}</p>
            <Button 
              variant="outline" 
              onClick={() => navigate('/blog')}
              className="mt-4"
            >
              {t('common.back')}
            </Button>
          </div>
        )}
      </div>
    </Layout>
  );
}
